import { cn, StarIcon, TrashIcon } from '@gramas/ui'
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import { useRef, useState } from 'react'

const ACTION_WIDTH = 88
/** Past this, releasing commits outright instead of resting the row open. */
const COMMIT = ACTION_WIDTH * 1.4
/** How far the row can be dragged at all, so a hard flick still feels bounded. */
const MAX = ACTION_WIDTH * 1.6

/**
 * Swipe a row sideways to act on it: left for Šalinti on a diary entry, right
 * for Mėgstamas on a catalogue product. Which sides exist is decided by which
 * handlers are passed, and a row given only `onDelete` behaves exactly as it
 * always has.
 *
 * The axis is decided once, at ten pixels, and never revisited mid-gesture —
 * vertical movement belongs to the scroller and is not touched. touch-action:
 * pan-y is what keeps the browser from claiming the horizontal moves for
 * scrolling before the handler sees them.
 */
export function SwipeableRow({
  onDelete,
  onFavourite,
  favourite = false,
  children,
}: {
  onDelete?: () => void
  onFavourite?: () => void
  /** Already starred, which is what the revealed icon should say. */
  favourite?: boolean
  children: ReactNode
}) {
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  const gesture = useRef<{
    startX: number
    startY: number
    baseDx: number
    axis: 'undecided' | 'x' | 'y'
  } | null>(null)
  const suppressClick = useRef(false)

  const commit = (run: (() => void) | undefined) => {
    setDragging(false)
    setDx(0)
    run?.()
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    // A swipe long enough to open the row is past the browser's tap slop, so no
    // click follows it on touch and the flag it set is never consumed — which
    // then swallowed the very tap on the revealed action the swipe was for. A
    // new gesture always precedes the click it needs to suppress, so clearing
    // it here is safe and clears exactly the stale case.
    suppressClick.current = false
    gesture.current = {
      startX: event.clientX,
      startY: event.clientY,
      baseDx: dx,
      axis: 'undecided',
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current
    if (!g) return
    const moveX = event.clientX - g.startX
    const moveY = event.clientY - g.startY
    if (g.axis === 'undecided') {
      if (Math.abs(moveX) < 10 && Math.abs(moveY) < 10) return
      g.axis = Math.abs(moveX) > Math.abs(moveY) ? 'x' : 'y'
      if (g.axis === 'x') {
        event.currentTarget.setPointerCapture(event.pointerId)
        setDragging(true)
      }
    }
    if (g.axis !== 'x') return
    // A side with no handler has no travel, so the row cannot be pulled open on
    // to an action that does not exist.
    setDx(Math.min(onFavourite ? MAX : 0, Math.max(onDelete ? -MAX : 0, g.baseDx + moveX)))
  }

  const onPointerEnd = () => {
    const g = gesture.current
    gesture.current = null
    if (g?.axis !== 'x') return
    // The drag must not fall through as a tap on the row underneath.
    suppressClick.current = true
    setDragging(false)
    if (dx <= -COMMIT) return commit(onDelete)
    if (dx >= COMMIT) return commit(onFavourite)
    setDx(dx <= -ACTION_WIDTH / 2 ? -ACTION_WIDTH : dx >= ACTION_WIDTH / 2 ? ACTION_WIDTH : 0)
  }

  return (
    <div
      className="relative overflow-hidden"
      style={{ touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onClickCapture={(event) => {
        if (suppressClick.current) {
          suppressClick.current = false
          event.preventDefault()
          event.stopPropagation()
          return
        }
        // A tap anywhere on an open row closes it rather than activating it —
        // except on the revealed action itself, which is the thing the row was
        // opened to reach. Without the exemption the capture phase swallowed
        // the button's own click and only a full swipe past COMMIT ever fired.
        if (dx !== 0 && !(event.target as HTMLElement).closest('[data-swipe-action]')) {
          event.preventDefault()
          event.stopPropagation()
          setDx(0)
        }
      }}
    >
      {/* `inert` while the row is closed: these buttons sit behind an opaque row
          and are unreachable by pointer, but a plain <button> is still in the
          tab order and still read aloud — which doubled the length of a
          forty-row catalogue list for anyone not using a thumb. */}
      {onFavourite && (
        <div
          data-swipe-action
          inert={dx === 0}
          className="absolute inset-y-0 left-0"
          style={{ width: ACTION_WIDTH }}
        >
          <button
            type="button"
            onClick={() => commit(onFavourite)}
            className="flex h-full w-full select-none flex-col items-center justify-center gap-0.5 bg-accent text-xs font-semibold text-accent-ink"
          >
            <StarIcon className="size-5" filled={favourite} />
            {favourite ? 'Pamiršti' : 'Mėgstamas'}
          </button>
        </div>
      )}
      {onDelete && (
        <div
          data-swipe-action
          inert={dx === 0}
          className="absolute inset-y-0 right-0"
          style={{ width: ACTION_WIDTH }}
        >
          <button
            type="button"
            onClick={() => commit(onDelete)}
            className="flex h-full w-full select-none flex-col items-center justify-center gap-0.5 bg-danger text-xs font-semibold text-white"
          >
            <TrashIcon className="size-5" />
            Šalinti
          </button>
        </div>
      )}
      <div
        className={cn(
          'relative bg-surface',
          !dragging && 'transition-transform duration-[140ms] ease-[var(--ease-out-quick)]',
        )}
        style={{ transform: `translateX(${dx}px)` }}
      >
        {children}
      </div>
    </div>
  )
}
